import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import template from 'lodash/template';
import PreviewCard from './PreviewCard';
import CardDeck from './CardDeck';
import { spycraftMixedArrType } from '../proptypes/spycraft';
import {
  mediaBreakpointUpMD,
  mediaBreakpointUpLG,
  mediaOnlyInternetExplorer,
  onlyInternetExplorer,
} from '../constants/style-constants';
import ProgressBar from './ProgressBar';
import LinkButton from './LinkButton';
import { hunit, vunit } from '../lib/style-helpers';
import PhrasesContext from './contexts/PhrasesContext';

const BackgroundCardDeck = styled(CardDeck)`
  filter: grayscale(1);

  @supports (display: grid) {
    grid-template-rows: auto 0 0 0;
    row-gap: 0;
    overflow: hidden;
  }

  /* stylelint-disable selector-max-compound-selectors */
  ${mediaOnlyInternetExplorer} {
    article {
      display: none;
    }

    article:first-of-type {
      display: flex;
    }
  }

  ${mediaBreakpointUpMD} and ${onlyInternetExplorer} {
    article:nth-of-type(-n+2) {
      display: flex;
    }
  }

  ${mediaBreakpointUpLG} and ${onlyInternetExplorer} {
    article {
      display: flex;
    }
  }
  /* stylelint-enable selector-max-compound-selectors */
`;

const PreviewProgressBar = styled(ProgressBar)`
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${vunit(2)};
  max-width: 450px;
  padding: 0 ${hunit(2)};
  width: 100%;

  ${mediaBreakpointUpMD} {
    padding-left: 0;
    padding-right: 0;
  }
`;

const LinkContainer = styled.div`
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-bottom: ${vunit(3)};
`;

const SpycraftPreviewDeck = ({
  content,
  onboardingChallengesLength,
  onboardingChallengesPlayed,
  nextGamePath,
}) => {
  const {
    progressBarLabel,
    playAllGames,
    playFirstGame,
    playNextGame,
  } = useContext(PhrasesContext);

  const buttonTitle = onboardingChallengesPlayed > 0 ? playNextGame : playFirstGame;

  return (
    <div className="position-relative">
      <BackgroundCardDeck cardsPerRow={4}>
        {
          content.map(({ path, ...props }) => (
            <PreviewCard
              // eslint-disable-next-line react/prop-types
              key={props.id}
              {...props}
              showDescription={false}
            />
          ))
        }
      </BackgroundCardDeck>
      <Overlay>
        <PreviewProgressBar
          label={progressBarLabel}
          total={onboardingChallengesLength}
          count={onboardingChallengesPlayed}
        />
        <p>
          {template(playAllGames)({ total: onboardingChallengesLength })}
        </p>
        <LinkContainer>
          <LinkButton className="btn-primary btn-block" title={buttonTitle} url={nextGamePath} />
        </LinkContainer>
      </Overlay>
    </div>
  );
};

SpycraftPreviewDeck.propTypes = {
  content: spycraftMixedArrType.isRequired,
  onboardingChallengesLength: PropTypes.number.isRequired,
  onboardingChallengesPlayed: PropTypes.number.isRequired,
  nextGamePath: PropTypes.string.isRequired,
};

export default SpycraftPreviewDeck;
